import { Plus } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import {
  FORM_CONTROL_BASE_CLASS,
  FORM_CONTROL_FOCUS_CLASS,
} from '@/components/ui/form-control-styles'
import {
  FormAccordion,
  FormAccordionCard,
  FormBadge,
  FormFieldsCountBadge,
  FormInnerBlock,
} from '@/components/contracts/ContractFormAccordion'
import { cn } from '@/lib/utils'
import {
  PLANNING_FREQUENCY_LABELS,
  WEEKDAYS,
  WEEKDAY_LABELS,
  type PlanningFrequency,
  type Weekday,
} from '@/types'
import { PLANNING_CARD_FREQUENCIES, TIME_SLOTS } from './constants'
import {
  FieldError,
  FormField,
  FrequencyCard,
  RequiredLabel,
  SelectionChip,
} from './FormControls'
import { chipShadow } from './styles'
import type { CustomSlot } from './types'

const figmaLabelClass =
  'flex h-4 w-full shrink-0 items-center gap-1 overflow-hidden whitespace-nowrap text-xs font-medium normal-case leading-4 text-[#171a20]'

const figmaFieldClass = cn(
  'h-10 rounded-xl border px-4 py-3 normal-case leading-[1.4] shadow-none placeholder:font-normal placeholder:text-[#6c7285]',
  FORM_CONTROL_BASE_CLASS,
  FORM_CONTROL_FOCUS_CLASS
)

const figmaSelectionChipClass = cn('text-xs font-normal normal-case leading-4')

interface Props {
  incompleteFields: number
  errors: {
    planning_frequency?: string
    interventions_per_year?: string
    preferred_days?: string
    preferred_time_slots?: string
    custom_slot_start?: string
    custom_slot_end?: string
  }
  clearError: (field: string) => void
  planningFrequency: PlanningFrequency | ''
  setPlanningFrequency: (value: PlanningFrequency | '') => void
  interventionsPerYear: string
  setInterventionsPerYear: (value: string) => void
  preferredDays: Weekday[]
  setPreferredDays: (next: Weekday[] | ((prev: Weekday[]) => Weekday[])) => void
  selectedSlots: number[]
  setSelectedSlots: (next: number[] | ((prev: number[]) => number[])) => void
  customSlot: CustomSlot
  setCustomSlot: (next: CustomSlot | ((prev: CustomSlot) => CustomSlot)) => void
}

export function PlanningSection({
  incompleteFields,
  errors,
  clearError,
  planningFrequency,
  setPlanningFrequency,
  interventionsPerYear,
  setInterventionsPerYear,
  preferredDays,
  setPreferredDays,
  selectedSlots,
  setSelectedSlots,
  customSlot,
  setCustomSlot,
}: Props) {
  const { t } = useTranslation('contracts')

  return (
    <FormAccordionCard
      value='planning'
      step={3}
      title={t('form.sections.planningGroup')}
      complete={incompleteFields === 0}
      stepClassName='font-medium'
      titleClassName='font-semibold normal-case'
      badge={
        incompleteFields === 0 ? (
          <span className='flex shrink-0 items-center gap-3'>
            {planningFrequency && planningFrequency !== 'custom' && (
              <FormBadge className='font-medium'>
                {PLANNING_FREQUENCY_LABELS[planningFrequency]}
              </FormBadge>
            )}
            <FormBadge className='font-medium'>
              {t('form.planning.visitsPerYearBadge', {
                count: Number(interventionsPerYear) || 0,
              })}
            </FormBadge>
            <FormBadge className='font-medium'>
              {t('form.planning.daysCount', {
                count: preferredDays.length,
              })}
            </FormBadge>
          </span>
        ) : (
          <FormFieldsCountBadge
            count={incompleteFields}
            label={t('form.fieldsToFill', {
              count: incompleteFields,
            })}
            completedLabel={t('form.fieldsFilled')}
            className='font-medium'
          />
        )
      }
    >
      <FormAccordion
        defaultValue={['intervention-planning']}
        className='flex flex-col gap-4'
      >
        <FormInnerBlock
          value='intervention-planning'
          title={t('form.sections.planning')}
        >
          <FormField gap='md'>
            <RequiredLabel compact className='font-medium'>
              {t('form.planning.frequency')}
            </RequiredLabel>
            <div
              aria-invalid={!!errors.planning_frequency}
              className={cn(
                'grid w-full grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-5',
                errors.planning_frequency
              )}
            >
              {PLANNING_CARD_FREQUENCIES.map(opt => (
                <FrequencyCard
                  key={opt.value}
                  selected={planningFrequency === opt.value}
                  onSelect={() => {
                    setPlanningFrequency(opt.value)
                    setInterventionsPerYear(String(opt.visits))
                    clearError('planning_frequency')
                    clearError('interventions_per_year')
                  }}
                  title={t(`form.planning.frequencyCard.${opt.value}.label`)}
                  hint={t(
                    `form.planning.frequencyCard.${opt.value}.description`
                  )}
                  titleClassName='text-sm font-medium normal-case'
                  hintClassName='text-xs normal-case leading-4'
                />
              ))}
            </div>
            <FieldError message={errors.planning_frequency} />
          </FormField>

          <FormField>
            <RequiredLabel
              htmlFor='interventions'
              compact
              className='font-medium'
            >
              {t('form.planning.interventionsPerYear')}
            </RequiredLabel>
            <Input
              id='interventions'
              type='number'
              min={0}
              max={366}
              placeholder='--'
              value={interventionsPerYear}
              aria-invalid={!!errors.interventions_per_year}
              onChange={e => {
                setInterventionsPerYear(e.target.value)
                if (Number(e.target.value) > 0) {
                  clearError('interventions_per_year')
                }
              }}
              className={cn(
                figmaFieldClass,
                errors.interventions_per_year && 'border-destructive'
              )}
            />
            <FieldError message={errors.interventions_per_year} />
          </FormField>

          <FormField gap='md'>
            <RequiredLabel compact className='font-medium'>
              {t('form.planning.workingDays')}
            </RequiredLabel>
            <div
              aria-invalid={!!errors.preferred_days}
              className={cn(
                'flex flex-wrap items-center gap-2',
                errors.preferred_days
              )}
            >
              {WEEKDAYS.map(day => {
                const selected = preferredDays.includes(day)
                return (
                  <SelectionChip
                    key={day}
                    selected={selected}
                    onSelect={() => {
                      setPreferredDays(prev =>
                        selected ? prev.filter(d => d !== day) : [...prev, day]
                      )
                      if (!selected) clearError('preferred_days')
                    }}
                    className={figmaSelectionChipClass}
                  >
                    {WEEKDAY_LABELS[day]}
                  </SelectionChip>
                )
              })}
              <button
                type='button'
                onClick={() => {
                  setPreferredDays([...WEEKDAYS])
                  clearError('preferred_days')
                }}
                className={cn(
                  'px-3 py-3 text-sm font-medium normal-case leading-none text-[#ff5a1f] hover:underline'
                )}
              >
                {t('form.planning.selectAll')}
              </button>
            </div>
            <FieldError message={errors.preferred_days} />
          </FormField>

          <FormField gap='md'>
            <RequiredLabel compact className='font-medium'>
              {t('form.planning.timeSlot')}
            </RequiredLabel>
            <div
              aria-invalid={!!errors.preferred_time_slots}
              className='flex flex-wrap items-center gap-2'
            >
              {TIME_SLOTS.map((s, i) => {
                const selected = selectedSlots.includes(i)
                return (
                  <SelectionChip
                    key={`${s.start}-${s.end}`}
                    selected={selected}
                    onSelect={() => {
                      setSelectedSlots(prev =>
                        selected ? prev.filter(x => x !== i) : [...prev, i]
                      )
                      if (!selected) clearError('preferred_time_slots')
                    }}
                    className={figmaSelectionChipClass}
                  >
                    {s.start} - {s.end}
                  </SelectionChip>
                )
              })}
              <button
                type='button'
                aria-pressed={!!customSlot}
                onClick={() => {
                  setCustomSlot(customSlot ? null : { start: '', end: '' })
                  if (customSlot) {
                    clearError('custom_slot_start')
                    clearError('custom_slot_end')
                  }
                }}
                className={cn(
                  'inline-flex items-center gap-2 rounded-xl border border-dashed px-4 py-2.5 text-sm font-semibold leading-[1.4]',
                  chipShadow,
                  customSlot
                    ? 'border-[#059669] bg-[#ecfdf5] text-[#171a20]'
                    : 'border-[#b0b5c3] bg-white text-[#3d434e]',
                  'text-xs font-medium normal-case leading-4',
                  FORM_CONTROL_FOCUS_CLASS
                )}
              >
                <Plus className='size-4' />
                {t('form.planning.other')}
              </button>
            </div>
            <FieldError message={errors.preferred_time_slots} />
            {customSlot && (
              <div className='grid max-w-md grid-cols-2 gap-4'>
                <FormField>
                  <RequiredLabel compact className={figmaLabelClass}>
                    {t('form.planning.startTime')}
                  </RequiredLabel>
                  <Input
                    type='time'
                    aria-label={t('form.planning.startTime')}
                    aria-invalid={!!errors.custom_slot_start}
                    value={customSlot.start}
                    onChange={e => {
                      setCustomSlot(s => ({
                        start: e.target.value,
                        end: s?.end ?? '',
                      }))
                      if (e.target.value) clearError('custom_slot_start')
                      if (e.target.value && customSlot.end) {
                        clearError('preferred_time_slots')
                      }
                    }}
                    className={cn(
                      figmaFieldClass,
                      errors.custom_slot_start && 'border-destructive'
                    )}
                  />
                  <FieldError message={errors.custom_slot_start} />
                </FormField>
                <FormField>
                  <RequiredLabel compact className={figmaLabelClass}>
                    {t('form.planning.endTime')}
                  </RequiredLabel>
                  <Input
                    type='time'
                    aria-label={t('form.planning.endTime')}
                    aria-invalid={!!errors.custom_slot_end}
                    value={customSlot.end}
                    onChange={e => {
                      setCustomSlot(s => ({
                        start: s?.start ?? '',
                        end: e.target.value,
                      }))
                      if (e.target.value) clearError('custom_slot_end')
                      if (customSlot.start && e.target.value) {
                        clearError('preferred_time_slots')
                      }
                    }}
                    className={cn(
                      figmaFieldClass,
                      errors.custom_slot_end && 'border-destructive'
                    )}
                  />
                  <FieldError message={errors.custom_slot_end} />
                </FormField>
              </div>
            )}
          </FormField>
        </FormInnerBlock>
      </FormAccordion>
    </FormAccordionCard>
  )
}
